import { Ruler, Maximize, ArrowUpFromLine, BadgeCheck, ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { scrollToId } from "@/App";

const SPECS = [
  {
    icon: Ruler,
    k: "PLAYING LINES",
    v: "13.41m × 6.10m",
    d: "Official pickleball court size — the lines themselves.",
  },
  {
    icon: Maximize,
    k: "RECOMMENDED FOOTPRINT",
    v: "18.3m × 9.1m",
    d: "Includes safe run-off zones. Tight fit possible from about 16.5m × 8m.",
  },
  {
    icon: ArrowUpFromLine,
    k: "CEILING CLEARANCE",
    v: "5m+ indoors",
    d: "Lower clearances still work for social play — tell us your venue.",
  },
];

const MONO = { fontFamily: '"JetBrains Mono", monospace', letterSpacing: 2 };

const CourtDiagram = () => (
  <svg viewBox="0 0 760 440" className="h-auto w-full" data-testid="dimensions-diagram" role="img" aria-label="Pickleball court dimensions diagram">
    {/* recommended footprint */}
    <rect x="50" y="70" width="659" height="328" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" strokeDasharray="7 7" rx="4" />
    <text x="709" y="56" textAnchor="end" fill="#94A3B8" fontSize="11" style={MONO}>RECOMMENDED FOOTPRINT 18.3m × 9.1m (INCL. RUN-OFF)</text>

    {/* court surface */}
    <rect x="138" y="124" width="483" height="220" fill="#171A23" stroke="#D4FF00" strokeWidth="2.5" rx="2" />

    {/* non-volley zones */}
    <rect x="303" y="124" width="77" height="220" fill="#D4FF00" opacity="0.07" />
    <rect x="380" y="124" width="77" height="220" fill="#D4FF00" opacity="0.07" />
    <line x1="303" y1="124" x2="303" y2="344" stroke="#D4FF00" strokeWidth="2" />
    <line x1="457" y1="124" x2="457" y2="344" stroke="#D4FF00" strokeWidth="2" />

    {/* service centre lines */}
    <line x1="138" y1="234" x2="303" y2="234" stroke="#D4FF00" strokeWidth="1.5" />
    <line x1="457" y1="234" x2="621" y2="234" stroke="#D4FF00" strokeWidth="1.5" />

    {/* net */}
    <line x1="380" y1="116" x2="380" y2="352" stroke="#FFFFFF" strokeOpacity="0.75" strokeWidth="3" />
    <text x="388" y="112" fill="rgba(255,255,255,0.5)" fontSize="10" style={MONO}>NET</text>

    {/* 13.41m dimension */}
    <line x1="138" y1="104" x2="621" y2="104" stroke="#64748B" strokeWidth="1" />
    <line x1="138" y1="100" x2="138" y2="108" stroke="#64748B" strokeWidth="1" />
    <line x1="621" y1="100" x2="621" y2="108" stroke="#64748B" strokeWidth="1" />
    <text x="379" y="96" textAnchor="middle" fill="#94A3B8" fontSize="12" style={MONO}>13.41m</text>

    {/* 6.10m dimension */}
    <line x1="118" y1="124" x2="118" y2="344" stroke="#64748B" strokeWidth="1" />
    <line x1="114" y1="124" x2="122" y2="124" stroke="#64748B" strokeWidth="1" />
    <line x1="114" y1="344" x2="122" y2="344" stroke="#64748B" strokeWidth="1" />
    <text transform="rotate(-90 106 234)" x="106" y="234" textAnchor="middle" fill="#94A3B8" fontSize="12" style={MONO}>6.10m</text>
  </svg>
);

export default function Dimensions() {
  return (
    <section id="dimensions" className="relative py-24 lg:py-32" data-testid="dimensions-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow="Court dimensions" title="How much space do you need?" testid="dimensions-heading" />

        <div className="grid gap-10 lg:grid-cols-[1.35fr_1fr] lg:items-start">
          <Reveal>
            <div className="rounded-3xl border border-white/10 bg-carbon p-6 sm:p-10">
              <CourtDiagram />
              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white/50">
                <span className="flex items-center gap-2">
                  <span className="h-0.5 w-6 bg-volt" /> Playing lines 13.41m × 6.10m
                </span>
                <span className="flex items-center gap-2">
                  <span className="h-0 w-6 border-t border-dashed border-white/40" /> Recommended footprint
                </span>
                <span className="flex items-center gap-2">
                  <span className="h-3 w-3 bg-volt/20 ring-1 ring-volt/40" /> Non-volley zone
                </span>
              </div>
            </div>
          </Reveal>

          <div className="space-y-4">
            {SPECS.map((s, i) => (
              <Reveal key={s.k} delay={i * 0.08}>
                <div
                  data-testid="dimensions-spec-card"
                  className="flex items-start gap-5 rounded-3xl border border-white/10 bg-panel p-6 transition-colors duration-300 hover:border-volt/40 sm:p-7"
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-volt/10 text-volt">
                    <s.icon size={20} />
                  </div>
                  <div>
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-volt">{s.k}</p>
                    <p className="mt-1 font-display text-2xl font-extrabold uppercase tracking-tight text-white">{s.v}</p>
                    <p className="mt-1 text-sm text-white/55">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}

            <Reveal delay={0.24}>
              <div className="rounded-3xl bg-volt p-8 text-obsidian lg:p-9" data-testid="joola-surface-card">
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em]">Court surfacing</p>
                <h3 className="mt-3 font-display text-2xl font-extrabold uppercase leading-none tracking-tight sm:text-3xl">
                  JOOLA pro-grade. Top of the range.
                </h3>
                <p className="mt-3 text-sm font-medium text-obsidian/80 sm:text-base">
                  Every PopUp court is finished with JOOLA-branded professional surfacing — the brand the world's
                  best players trust. True, consistent bounce and confident grip underfoot, indoors or out.
                </p>
                <ul className="mt-5 space-y-2">
                  {["True, consistent ball bounce", "Pro-grade grip and traction", "Indoor and outdoor rated"].map((t) => (
                    <li key={t} className="flex items-center gap-2 text-sm font-semibold">
                      <BadgeCheck size={16} className="shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1} className="mt-12">
          <div className="flex flex-col items-start gap-5 rounded-3xl border border-volt/25 bg-volt/5 p-7 sm:flex-row sm:items-center sm:justify-between sm:p-8">
            <p className="max-w-xl text-sm text-white/70 sm:text-base">
              Not sure your space fits? Send us your venue measurements with your quote request and we'll confirm
              within the hour.
            </p>
            <button
              data-testid="dimensions-quote-link"
              onClick={() => scrollToId("#quote")}
              className="group flex shrink-0 items-center gap-2 rounded-full bg-volt px-6 py-3 font-display text-base font-bold uppercase tracking-wide text-obsidian transition-colors hover:bg-volt-hover"
            >
              Check my space
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
